import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import type { ComposeViewState } from 'owa-mail-compose-store';
import { getComposeViewState } from 'owa-mail-reading-pane-store/lib/utils/getComposeViewState';
import { RibbonControlDefinitionProps } from 'owa-mail-ribbon-utils';
import type { RibbonControlId } from 'owa-ribbon-ids';
import { FormatControlId } from 'owa-ribbon-ids';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import { getTableOperationMenu } from 'owa-compose-formatcontrols/lib/components/tableControls/getTableOperationMenu';
import { getTableOperationControl } from 'owa-compose-formatcontrols/lib/components/tableControls/getTableOperationControl';
import { getTablePredefinedStyleMenu } from 'owa-compose-formatcontrols/lib/components/tableControls/getTablePredefinedStyleMenu';
import { getTableFormatStateMenu } from 'owa-compose-formatcontrols/lib/components/tableControls/getTableFormatStateMenu';
import TableSettingsRegular from 'owa-fluent-icons-svg/lib/icons/TableSettingsRegular';
import TableCellEditRegular from 'owa-fluent-icons-svg/lib/icons/TableCellEditRegular';
import { getCellShadingControl } from 'owa-compose-formatcontrols/lib/components/tableControls/getCellShadingControl';
import { getTableFormatTabControlIcon } from 'owa-compose-formatcontrols/lib/components/tableControls/tableFormatTabControlIconMap';

/**
 * This function supplies a mapping from RibbonControlId to their respective control definition function.
 * For any new control in a tab, be sure to update the mapping below.
 * Note: This function should *not* have any logic regarding showing/hiding.
 *       That should be in shouldShowRibbonControl.ts instead.
 */
export const getMLRTableFormatTabControlDefinition = computedFn(
    (
        controlId: RibbonControlId,
        props: RibbonControlDefinitionProps
    ): RibbonControlProps | undefined => {
        const viewState: ComposeViewState | undefined =
            props.popoutComposeViewState ?? getComposeViewState();
        if (!viewState) {
            return undefined;
        }
        const editorId = viewState.editorId;
        const defaultStyles = getDefaultRibbonStyles();

        let icon: string | undefined = getTableFormatTabControlIcon(controlId);

        switch (controlId) {
            case FormatControlId.DeleteTableMenu:
            case FormatControlId.TableMergeCellsMenu:
            case FormatControlId.TableSplitCellsMenu:
            case FormatControlId.TableInsertMenu:
            case FormatControlId.TableAlignCellVerticallyMenu:
            case FormatControlId.TableAlignCellHorizontallyMenu:
                return getTableOperationMenu(
                    editorId,
                    controlId,
                    defaultStyles,
                    undefined /* afterExecuteCallback */,
                    icon
                );
            case FormatControlId.InsertRowAbove:
            case FormatControlId.InsertRowBelow:
            case FormatControlId.InsertColumnLeft:
            case FormatControlId.InsertColumnRight:
            case FormatControlId.DeleteTable:
            case FormatControlId.DeleteColumns:
            case FormatControlId.DeleteRows:
            case FormatControlId.MergeAbove:
            case FormatControlId.MergeBelow:
            case FormatControlId.MergeLeft:
            case FormatControlId.MergeRight:
            case FormatControlId.SplitCellsHorizontally:
            case FormatControlId.SplitCellsVertically:
            case FormatControlId.AlignTableCellCenter:
            case FormatControlId.AlignTableCellLeft:
            case FormatControlId.AlignTableCellRight:
            case FormatControlId.AlignTableCellTop:
            case FormatControlId.AlignTableCellMiddle:
            case FormatControlId.AlignTableCellBottom:
                return getTableOperationControl(
                    editorId,
                    controlId,
                    defaultStyles,
                    icon,
                    undefined /* afterExecuteCallback */
                );
            case FormatControlId.PredefinedStyles:
                return getTablePredefinedStyleMenu(editorId, TableCellEditRegular);
            case FormatControlId.TableStyleOptions:
                return getTableFormatStateMenu(
                    editorId,
                    viewState.ribbon,
                    defaultStyles,
                    TableSettingsRegular
                );

            case FormatControlId.CellShading:
                return getCellShadingControl(editorId, viewState.ribbon, undefined);

            default:
                throw new Error(
                    'Unknown control: ' +
                        controlId +
                        ' searched for in getMLRTableFormatTabControlDefinition'
                );
        }
    }
);
